import { useAppDeps, useSDK } from '@features/app-deps-provider'
import { useAuthState } from '@features/app-hooks'
import { documentUploadsModule, uploadDocument } from '@features/document-uploads'
import { Link } from '@features/link'
import { platformModule } from '@features/st-pdf-viewer/platform-module'
import { useDevMode } from '@st/react-util/dev-mode'
import { Folder, FolderTag, ScopedFolderMembership, STDocument } from '@st/sdk'
import {
  ActivityFeedItem,
  ActivityFeedList,
  ArrowRightIcon,
  Button,
  ContextMenuItem,
  DownloadIcon,
  EyeIcon,
  FullScreenDropZoneBarrier,
  LinkIcon,
  Modal,
  PopoverMenu,
  PrimaryContextMenu,
  RowsIcon,
  SendIconLarge,
  TagChip,
  Tip,
  ToolbarSectionDivider,
  useFileDrop
} from '@st/theme'
import { formatPercentage } from '@st/util/format'
import { parseMimeType } from '@st/util/mime'
import { formatISO8601RelativeDateTime } from '@st/util/time'
import { uuidv7 } from '@st/util/uuidv7'
import { useEffect, useState } from 'react'
import { match, P } from 'ts-pattern'
import { routes } from '~/features/routes'
import { STPDFDocViewer } from '~/st-doc-viewer'
import { useProcess, useProcessState } from '~/store'
import { getStatusColor, getStatusLabel } from '../st-folder-list/common'
import { stOrganizationModule } from '../st-organizations/st-organization-module'
import { EditFolderTagsDialog } from './edit-folder-tags-dialog'
import { FolderTagsList, FolderTagsListEditButton } from './folder-tags-list'
import { SendClientOrganizerDialog } from './send-client-organizer-dialog'
import { STBookmarksSidebar } from './st-bookmarks-sidebar'
import { STCopyShareableLinkDialog } from './st-copy-shareable-link-dialog'
import { stDownloadDocumentHandler } from './st-download-document-handler'
import { STDownloadFolderDialog } from './st-download-folder-dialog'
import {
  deleteAllDocuments,
  ExpandedActivityFeedEvent,
  formatFolderEntityName,
  selActivityFeedEvents,
  selSelectedDoc,
  STFolderMessage,
  stFolderModule
} from './st-folder-module'
import { StFolderViewerDebug } from './st-folder-viewer-debug'
import { STFormationChecklistViewer } from './st-formation-checklist-viewer'
import { STFormationViewer } from './st-formation-viewer'
import { updatedDocumentSplits } from './update-document-splits'
import { humanJoin } from '@st/util/inflect'

type FolderAction =
  | { type: 'openSendQuestionnaireDialog' }
  | { type: 'downloadFolderDialog' }
  | { type: 'copyWorkpaperLink' }
  | { type: 'openCopyShareableLinkDialog' }
  | { type: 'previewQuestionnaire' }
  | { type: 'uploadDocuments'; dataTransfer: DataTransfer }
  | { type: 'deleteAllDocuments' }
  | { type: 'editFolderTags' }

type Props = {
  folderId: string
  selectedDocId?: string
  onSelect?: (doc: STDocument) => void
  debug?: boolean
}
export function STFolderViewer({ folderId, debug }: Props) {
  const sdk = useSDK()
  const { socket } = useAppDeps()
  const { user } = useAuthState()

  const organizationFolderTags = useProcessState(
    stOrganizationModule,
    (s) => s.state?.folderTags ?? []
  )

  const documentUploads = useProcess(documentUploadsModule)
  const stFolder = useProcess(stFolderModule, { currentUserId: user!.id })
  const platform = useProcess(platformModule)

  const folderLoaded = useProcessState(stFolderModule, (s) => s.folderState?.folder != undefined)
  const organizationSlug = useProcessState(stFolderModule, (s) => s.folderState?.organization.slug)
  const selectedDoc = useProcessState(stFolderModule, selSelectedDoc)
  const folderEntities = useProcessState(stFolderModule, (s) => s.folderState?.folder.entities)
  const folderTagIds = useProcessState(stFolderModule, (s) => s.folderState?.folder.tagIds ?? [])
  const clientOrganizerStatus = useProcessState(
    stFolderModule,
    (s) => s.folderState?.folder.clientOrganizer.status
  )
  const questionnaireProgress = useProcessState(
    stFolderModule,
    (s) => s.folderState?.folder.clientOrganizer.questionnaireProgress ?? 0
  )

  const [action, setAction] = useState<FolderAction | undefined>()

  useEffect(() => {
    const unsub = socket.subscribe(`folder:${folderId}`, {
      load: (e) => {
        stFolder.send({ type: 'stateLoad', vsn: e.vsn, state: e.state })
      },
      diff: (e) => {
        stFolder.send({ type: 'stateDiff', fromVsn: e.fromVsn, toVsn: e.toVsn, diff: e.diff })
      }
    })

    return () => unsub()
  }, [socket, folderId, stFolder])

  useEffect(() => {
    if (folderLoaded) {
      // if folder isn't loaded we don't conside it viewed
      sdk.send({ type: 'folders/trackFolderViewed', folderId: folderId })
    }
  }, [sdk, folderId, folderLoaded])

  if (debug) {
    return <StFolderViewerDebug />
  }

  return (
    <STFolderViewerInner
      folderEntities={folderEntities}
      action={action}
      folderId={folderId}
      clientOrganizerStatus={clientOrganizerStatus}
      questionnaireProgress={questionnaireProgress}
      selectedDoc={selectedDoc}
      loading={!folderLoaded}
      organizationFolderTags={organizationFolderTags}
      folderTagIds={folderTagIds}
      onAction={(action) => {
        switch (action?.type) {
          case 'copyWorkpaperLink':
            platform.send({ type: 'copyToClipboard', text: window.location.href })
            platform.send({
              type: 'showSnackbar',
              message: 'Copied workpaper link to clipboard'
            })
            break
          case 'previewQuestionnaire':
            window.open(
              routes.toPath({
                name: 'organization_preview_questionnaire',
                organizationSlug: organizationSlug,
                folderId: folderId
              })
            )
            break
          case 'uploadDocuments':
            for (const file of action.dataTransfer.files) {
              documentUploads.send(
                uploadDocument({
                  uploadId: uuidv7(),
                  file,
                  folderId: folderId
                })
              )
            }
            break
          case 'deleteAllDocuments':
            stFolder.send(deleteAllDocuments(undefined))
            platform.send({
              type: 'showSnackbar',
              message: 'Deleted all documents'
            })
            break
          default:
            setAction(action)
        }
      }}
      send={stFolder.send}
    />
  )
}

function STFolderViewerInner({
  folderId,
  folderEntities,
  selectedDoc,
  clientOrganizerStatus,
  questionnaireProgress,
  action,
  organizationFolderTags,
  folderTagIds,
  loading,
  onAction,
  send
}: {
  folderId: string
  folderEntities: Folder['entities'] | undefined
  clientOrganizerStatus: Folder['clientOrganizer']['status'] | undefined
  questionnaireProgress: Folder['clientOrganizer']['questionnaireProgress']
  selectedDoc: STDocument | undefined
  action: FolderAction | undefined
  organizationFolderTags: FolderTag[]
  folderTagIds: string[]
  loading: boolean
  onAction: (action: FolderAction | undefined) => void
  send: (message: STFolderMessage) => void
}) {
  function onDrop(dataTransfer: DataTransfer) {
    onAction({ type: 'uploadDocuments', dataTransfer: dataTransfer })
  }
  const { isDragging, dropHandlers } = useFileDrop(onDrop)
  const devMode = useDevMode()

  const entityName = formatFolderEntityName(folderEntities, 'client')

  return (
    <div
      className="grid h-full w-full grid-rows-[50px_auto] overflow-hidden"
      style={{ minWidth: '1200px' }}
      {...dropHandlers}
    >
      {isDragging && <FullScreenDropZoneBarrier />}
      <div className="bg-white-200 flex flex-row items-center justify-between border-b border-stone-200 px-5 py-3">
        {/* Breadcrumb */}
        <div className="group flex flex-row items-center gap-2">
          <Link href={{ name: 'folder_list' }} className="text-lg text-gray-600">
            Binders
          </Link>
          {entityName ? <ArrowRightIcon className="size-4 text-gray-500" /> : null}
          {entityName ? (
            <span className="text-lg font-bold text-gray-900">{`${entityName}'s Binder`}</span>
          ) : null}

          <div className="ml-2 flex flex-row items-center gap-2">
            <FolderTagsList
              folderTags={organizationFolderTags}
              tagIds={folderTagIds}
              onClick={() => onAction({ type: 'editFolderTags' })}
            />

            <FolderTagsListEditButton onClick={() => onAction({ type: 'editFolderTags' })} />
          </div>
        </div>

        <div className="flex flex-row items-stretch gap-2">
          <div className="flex flex-row items-center gap-2">
            <div className="text-sm text-gray-500">Client Organizer</div>

            {clientOrganizerStatus && (
              <TagChip
                color={getStatusColor(clientOrganizerStatus)}
                className="flex items-center gap-2"
              >
                {getStatusLabel(clientOrganizerStatus)}
                {clientOrganizerStatus == 'in_progress' && (
                  <div>{formatPercentage(questionnaireProgress)}%</div>
                )}
              </TagChip>
            )}

            <Tip title="Preview client organizer">
              <Button
                variant="icon"
                leadingIcon={<EyeIcon className="size-4" />}
                onClick={() => onAction({ type: 'previewQuestionnaire' })}
                disabled={loading}
              />
            </Tip>

            <Tip title="Shareable client organizer link">
              <Button
                variant="icon"
                leadingIcon={<LinkIcon className="size-4" />}
                onClick={() => onAction({ type: 'openCopyShareableLinkDialog' })}
                disabled={loading}
              />
            </Tip>

            <Tip title="Send to client">
              <Button
                variant="icon"
                leadingIcon={<SendIconLarge className="size-4" />}
                onClick={() => onAction({ type: 'openSendQuestionnaireDialog' })}
                disabled={loading}
              />
            </Tip>
          </div>

          <ToolbarSectionDivider />

          <div className="flex flex-row items-center gap-2">
            <div className="text-sm text-gray-500">Binder</div>
            <Tip title="Export binder">
              <Button
                variant="icon"
                leadingIcon={<DownloadIcon className="size-4" />}
                onClick={() => onAction({ type: 'downloadFolderDialog' })}
                disabled={loading}
              />
            </Tip>

            <ActivityFeedButton />

            <PrimaryContextMenu>
              {clientOrganizerStatus == 'submitted' ? (
                <ContextMenuItem
                  onClick={() =>
                    send({
                      type: 'request',
                      request: { type: 'folders/unlockClientOrganizer', folderId: folderId }
                    })
                  }
                >
                  Unlock client organizer
                </ContextMenuItem>
              ) : (
                <ContextMenuItem
                  onClick={() =>
                    send({
                      type: 'request',
                      request: {
                        type: 'folders/submitClientOrganizer',
                        folderId: folderId,
                        forceSubmit: true
                      }
                    })
                  }
                >
                  Submit organizer on behalf of client
                </ContextMenuItem>
              )}

              <ContextMenuItem onClick={() => onAction({ type: 'copyWorkpaperLink' })}>
                Copy firm workpaper link
              </ContextMenuItem>

              {devMode && (
                <ContextMenuItem onClick={() => onAction({ type: 'deleteAllDocuments' })}>
                  Delete all documents
                </ContextMenuItem>
              )}
            </PrimaryContextMenu>
          </div>
        </div>
      </div>
      <div className="grid h-full w-full grid-cols-[300px_auto] overflow-hidden">
        <STBookmarksSidebar />

        {selectedDoc ? (
          <STDocViewer key={selectedDoc.id} folderId={folderId} doc={selectedDoc} send={send} />
        ) : (
          <></>
        )}
      </div>

      <Modal isOpen={action != undefined}>
        {match(action)
          .with({ type: 'openSendQuestionnaireDialog' }, () => (
            <SendClientOrganizerDialog folderId={folderId} onClose={() => onAction(undefined)} />
          ))
          .with({ type: 'downloadFolderDialog' }, () => (
            <STDownloadFolderDialog folderId={folderId} onClose={() => onAction(undefined)} />
          ))
          .with({ type: 'openCopyShareableLinkDialog' }, () => (
            <STCopyShareableLinkDialog folderId={folderId} onClose={() => onAction(undefined)} />
          ))
          .with({ type: 'editFolderTags' }, () => (
            <EditFolderTagsDialog
              folderId={folderId}
              tagIds={folderTagIds}
              folderEntities={folderEntities}
              onClose={() => onAction(undefined)}
            />
          ))
          .otherwise(() => null)}
      </Modal>
    </div>
  )
}

/**
 * Depending on the type of document selected, we detect the appropriate viewer
 * and render that.
 */
function STDocViewer({
  doc,
  folderId,
  send
}: {
  doc: STDocument
  folderId: string
  send: (message: STFolderMessage) => void
}) {
  const mime = parseMimeType(doc.mimeType)

  function handleDeletePage(pageNumber: number) {
    send({
      type: 'request',
      request: {
        type: 'folders/deleteDocumentPages',
        folderId: folderId,
        documentId: doc.id,
        pageNumbers: [pageNumber]
      }
    })
  }

  function handleRestorePage(pageNumber: number) {
    send({
      type: 'request',
      request: {
        type: 'folders/restoreDocumentPages',
        folderId: folderId,
        documentId: doc.id,
        pageNumbers: [pageNumber]
      }
    })
  }

  function handleUpdateSplit(afterPageNumber: number, action: 'add' | 'remove') {
    send({
      type: 'request',
      request: {
        type: 'folders/updateDocumentSplits',
        folderId: folderId,
        documentId: doc.id,
        documentSplits: updatedDocumentSplits(doc, action, afterPageNumber)
      }
    })
  }

  function handleRotatePage(pageNumber: number, rotation: number) {
    send({
      type: 'request',
      request: {
        type: 'folders/updateDocumentPageRotation',
        folderId: folderId,
        documentId: doc.id,
        pageNumber: pageNumber,
        rotation: rotation
      }
    })
  }

  return match({ doc, mime })
    .with({ mime: { type: 'application', subtype: 'formation' } }, () => {
      return <STFormationViewer doc={doc} />
    })
    .with({ mime: { type: 'application', subtype: 'formation-checklist' } }, () => {
      return <STFormationChecklistViewer />
    })
    .with({ doc: { processingStatus: 'processing' } }, () => {
      return <DocumentSpinner caption="Processing..." />
    })
    .with({ doc: { passwordProtected: true } }, () => {
      return (
        <DownloadNonViewableFile
          folderId={folderId}
          doc={doc}
          warning="This document is password protected."
        />
      )
    })
    .with({ doc: { pdfPath: P.nonNullable } }, ({ doc }) => {
      return (
        <STPDFDocViewer
          doc={doc}
          folderId={folderId}
          send={send}
          onDeletePage={handleDeletePage}
          onRestorePage={handleRestorePage}
          onAddSplit={(afterPageNumber) => handleUpdateSplit(afterPageNumber, 'add')}
          onRemoveSplit={(afterPageNumber) => handleUpdateSplit(afterPageNumber, 'remove')}
          onRotatePage={(pageNumber, rotation) => handleRotatePage(pageNumber, rotation)}
        />
      )
    })
    .otherwise(() => {
      return <DownloadNonViewableFile folderId={folderId} doc={doc} />
    })
}

function DownloadNonViewableFile({
  folderId,
  doc,
  warning
}: {
  folderId: string
  doc: STDocument
  warning?: string
}) {
  const { sdk } = useAppDeps()
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-stone-200">
      <div className="flex h-1/4 w-1/4 flex-col items-center justify-center rounded-md bg-white p-4 shadow-md">
        <svg className="h-16 w-16" viewBox="0 0 73 72" fill="none">
          <rect x="0.5" width="72" height="72" rx="36" fill="#111536" fillOpacity="0.4" />
          <path
            d="M44.0306 31.7194L38.7806 26.4694C38.7109 26.3997 38.6282 26.3445 38.5371 26.3069C38.4461 26.2693 38.3485 26.2499 38.25 26.25H29.25C28.8522 26.25 28.4706 26.408 28.1893 26.6893C27.908 26.9706 27.75 27.3522 27.75 27.75V44.25C27.75 44.6478 27.908 45.0294 28.1893 45.3107C28.4706 45.592 28.8522 45.75 29.25 45.75H42.75C43.1478 45.75 43.5294 45.592 43.8107 45.3107C44.092 45.0294 44.25 44.6478 44.25 44.25V32.25C44.2501 32.1515 44.2307 32.0539 44.1931 31.9629C44.1555 31.8718 44.1003 31.7891 44.0306 31.7194ZM39 28.8103L41.6897 31.5H39V28.8103ZM42.75 44.25H29.25V27.75H37.5V32.25C37.5 32.4489 37.579 32.6397 37.7197 32.7803C37.8603 32.921 38.0511 33 38.25 33H42.75V44.25Z"
            fill="white"
          />
        </svg>

        <div className="mt-4 break-all text-center font-semibold text-gray-700">{doc.name}</div>

        {warning ? (
          <div className="mb-2 mt-2 break-all text-center text-sm text-gray-700">{warning}</div>
        ) : null}

        <Button
          variant="primary"
          className="mt-4"
          leadingIcon={<DownloadIcon className="size-4" />}
          onClick={() => {
            stDownloadDocumentHandler(doc, {
              sdk: sdk,
              folderId: folderId,
              format: 'source'
            })
          }}
        >
          Download
        </Button>
      </div>
    </div>
  )
}

function DocumentSpinner({ caption }: { caption?: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-stone-200">
      <div role="status">
        <svg
          aria-hidden="true"
          className="h-16 w-16 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
          viewBox="0 0 100 101"
          fill="none"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
      </div>
      <div className="text-center text-gray-700">{caption ?? ''}</div>
    </div>
  )
}

function ActivityFeedButton() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <PopoverMenu
      isOpen={isOpen}
      isControlled={true}
      onOpenChange={setIsOpen}
      trigger={
        <div>
          {/* workaround for button clicks getting swallowed by the popover menu */}
          {/* adding a button directly swallows the click event */}
          {/* TODO: look into PopoverMenu implementation and see how we want to fix this */}
          <Tip title="Activity feed">
            <Button
              variant="icon"
              leadingIcon={<RowsIcon className="size-4" />}
              onClick={() => setIsOpen(true)}
            />
          </Tip>
        </div>
      }
    >
      <ActivityFeedPopup onClose={() => setIsOpen(false)} />
    </PopoverMenu>
  )
}

function ActivityFeedPopup({ onClose }: { onClose: () => void }) {
  const events = useProcessState(stFolderModule, selActivityFeedEvents)

  return (
    <ActivityFeedList title="Activity" onClose={onClose}>
      {events.map((e) => (
        <ActivityFeedItem
          key={e.id}
          title={formatActor(e.actorMembership)}
          description={formatEventDescription(e)}
          time={formatISO8601RelativeDateTime(e.time)}
          warning={
            e.type == 'client_organizer_email_sent' && e.status == 'failed'
              ? `Failed to send: ${e.errorMessage}`
              : undefined
          }
        />
      ))}
    </ActivityFeedList>
  )
}

function formatActor(m: ScopedFolderMembership | undefined) {
  if (m?.scope == 'organization') return 'Firm'
  else if (m?.scope == 'folder') return 'Client'
  else return '<removed>'
}

function formatEventDescription(e: ExpandedActivityFeedEvent) {
  switch (e.type) {
    case 'document_uploaded':
      return (
        <span>
          Uploaded document <b>{e.document?.name}</b>
        </span>
      )
    case 'document_deleted':
      return (
        <span>
          Deleted document <b>{e.document?.name}</b>
        </span>
      )
    case 'client_organizer_started':
      return 'Started organizer'
    case 'client_organizer_submitted':
      return 'Submitted organizer'
    case 'client_organizer_email_sent':
      switch (e.messageType) {
        case 'client_organizer_invitation':
          return `Sent organizer to ${humanJoin(e.recipientEmails, 'and')}`
        case 'client_organizer_document_request':
          return `Sent document request to ${humanJoin(e.recipientEmails, 'and')}`
        case 'client_organizer_submit_reminder':
          return `Sent reminder to ${humanJoin(e.recipientEmails, 'and')}`
      }
    case 'documents_exported':
      return 'Exported documents'
  }
}
